import React, { useState, useEffect } from 'react';
import { 
  CardContent, 
  Typography, 
  TextField,
  InputAdornment,
  Box,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useAppContext } from '../context/AppContext';
import CONFIG from '../utils/config';
import { formatInWords } from '../utils/formatInWords';

function ExpensesInRetirementForm() {
  const current_year = new Date().getFullYear();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const { state, actions } = useAppContext();

  // Use a plain string for the raw input so the TextField stays controlled
  // while the user is mid-typing (e.g. "1,00" before completing "1,00,000")
  const [rawInput, setRawInput] = useState(
    state.monthlyExpensesInRetirement ? String(state.monthlyExpensesInRetirement) : ''
  );

  const formatNumber = (num) => new Intl.NumberFormat('en-IN').format(num);

  const formatCorpus = (amount) => {
    if (amount >= 10000000) return (amount / 10000000).toFixed(2) + ' Cr';
    if (amount >= 100000)   return (amount / 100000).toFixed(2) + ' Lakhs';
    return amount.toLocaleString('en-IN', { currency: 'INR', maximumFractionDigits: 0 });
  };

  // Corpus calculation
  useEffect(() => {
    if (!state.monthlyExpensesInRetirement || state.monthlyExpensesInRetirement <= 0) return;

    const monthlyInflationRate             = Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, 1 / 12) - 1;
    const postRetirementReturn             = CONFIG.DEFAULT_POST_RETIREMENT_RETURN[state.riskProfile]
                                             ?? CONFIG.DEFAULT_POST_RETIREMENT_RETURN['Conservative'];
    const monthlyPostRetirementDiscountRate= Math.pow(1 + postRetirementReturn, 1 / 12) - 1;
    const yearlyExpensesInRetirement       = state.monthlyExpensesInRetirement * 12;
    const yearsInRetirement                = CONFIG.DEFAULT_LIFE_EXPECTANCY - state.retirementAge;
    const retirementExpenses_FV            = yearlyExpensesInRetirement
                                             * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, state.yearsForRetirement);
    const corpusRequired                   = (retirementExpenses_FV / 12)
                                             * (1 - Math.pow(
                                                 (1 + monthlyInflationRate) / (1 + monthlyPostRetirementDiscountRate),
                                                 yearsInRetirement * 12
                                               ))
                                             / (monthlyPostRetirementDiscountRate - monthlyInflationRate);

    actions.updateFormData('corpusRequired', Math.round(corpusRequired));
  }, [state.monthlyExpensesInRetirement, state.riskProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const calculateCorpus = () => {
      const monthlyInflationRate = Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, 1 / 12) - 1;
      const postRetirementReturn = CONFIG.DEFAULT_POST_RETIREMENT_RETURN[state.riskProfile] ?? CONFIG.DEFAULT_POST_RETIREMENT_RETURN["Conservative"];
      const monthlyPostRetirementDiscountRate = Math.pow(1 + postRetirementReturn, 1 / 12) - 1;
      const yearlyExpensesInRetirement = state.monthlyExpensesInRetirement*12;
      const yearsInRetirement = CONFIG.DEFAULT_LIFE_EXPECTANCY - state.retirementAge
      const retirementExpenses_FV = yearlyExpensesInRetirement * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, state.yearsForRetirement)
      const corpusRequired = (retirementExpenses_FV/12) * (1 - Math.pow((1+monthlyInflationRate)/ (1 + monthlyPostRetirementDiscountRate), yearsInRetirement * 12)) / 
      (monthlyPostRetirementDiscountRate - monthlyInflationRate)
      actions.updateFormData('corpusRequired', Math.round(corpusRequired));
    };
  
    if (state.monthlyExpensesInRetirement > 0) {
      calculateCorpus();
    }
  }, [state.monthlyExpensesInRetirement, state.riskProfile]); // runs when component loads or monthlyExpensesInRetirement changes
  

  const handleChange = (e) => {
    const stripped = e.target.value.replace(/,/g, '');
    const parsed   = parseInt(stripped, 10);
    const numeric  = isNaN(parsed) ? 0 : parsed;

    // Show formatted value in field; keep raw numeric in context
    setRawInput(numeric > 0 ? formatNumber(numeric) : '');
    actions.updateFormData('monthlyExpensesInRetirement', numeric);
  };

  const displayedExpense = state.monthlyExpensesInRetirement || 0;

  return (
    <Box>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          In today's terms, how much would you spend per month to retire comfortably?
        </Typography>

        <Box sx={{ mt: 4 }}>
          <TextField
            label="Monthly Expenses For Retirement"
            value={rawInput}
            onChange={handleChange}
            fullWidth
            type="tel"
            inputMode="numeric"
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
          {displayedExpense >= 1000 && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {formatInWords(displayedExpense)}
            </Typography>
          )}
        </Box>

        <Stack spacing={2} sx={{ mt: 4 }}>
          {state.corpusRequired > 0 && (
            <>
              <Typography variant="body1" fontWeight="bold">
                You will need{' '}
                <span style={{ color: '#1976d2' }}>₹{formatCorpus(state.corpusRequired)}</span>{' '}
                in{' '}
                <span style={{ color: '#1976d2' }}>
                  {current_year + Number(state.yearsForRetirement === -1 ? 0 : state.yearsForRetirement)}
                </span>{' '}
                to secure your retirement. 🏌️
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<InfoIcon />}
                  onClick={() => setInfoDialogOpen(true)}
                />
              </Typography>
              <Typography variant="body1">
                Now let's plan your other financial goals.
              </Typography>
            </>
          )}
        </Stack>

        <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogContent>
            <Typography variant="body2" color="textSecondary">
              Assumptions:<br />
              Life expectancy: {CONFIG.DEFAULT_LIFE_EXPECTANCY} Years<br />
              Your expenses will inflate at {CONFIG.DEFAULT_INFLATION_RATE_STRING} per annum.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInfoDialogOpen(false)} variant="contained">Got it</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Box>
  );
}

export default ExpensesInRetirementForm;
